// app/components/cookies-policy/Hero.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

export default function Hero() {
  const t = useTranslations("CookiesPolicy");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  // แปลง pathname → path ที่ใช้ใน CMS (ตัด /en /zh ด้านหน้า)
  const pagePath = React.useMemo(() => {
    const parts = pathname.split("/");
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    return pathname || "/";
  }, [pathname]);

  // ----- default text จาก i18n (ดีต่อ SEO/SSR) -----
  const [title, setTitle] = React.useState(t("Hero.title"));
  const [lastUpdate, setLastUpdate] = React.useState(t("Hero.lastUpdate"));

  // locale เปลี่ยน → reset กลับค่า default
  React.useEffect(() => {
    setTitle(t("Hero.title"));
    setLastUpdate(t("Hero.lastUpdate"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);


  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          if (e.cmsKey) {
            map[e.cmsKey] = e.value;
            return;
          }
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            map[match[1]] = e.value;
          }
        });

        if (map["cookies.hero.title"]) setTitle(map["cookies.hero.title"]);
        if (map["cookies.hero.lastUpdate"])
          setLastUpdate(map["cookies.hero.lastUpdate"]);
      } catch (err) {
        console.warn("[CookiesPolicy Hero] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box className="policy-hero">
      <Container className="container">
        <Grid container>
          <Grid size={12}>
            <Typography
              variant="h1"
              data-aos="zoom-in"
              data-cms-key="cookies.hero.title"
              sx={{
                mb: 1,
                whiteSpace: { xs: "normal", lg: "pre-line" }
              }}>
              {title}
            </Typography>
            <Typography
              variant="body1"
              data-aos="zoom-in"
              data-cms-key="cookies.hero.lastUpdate"
            >
              {lastUpdate}
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}